import { useState, useEffect } from "react";
import { getUserYieldConsent, updateUserYieldConsent } from "@/goldhunter/api/entities";
import { User, AlertTriangle, ShieldCheck } from "lucide-react";

export default function Profile() {
  const [userId, setUserId] = useState(() => {
    let v = localStorage.getItem("yabai_user_id");
    if (!v) {
      v = `u-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("yabai_user_id", v);
    }
    return v;
  });
  const [consent, setConsent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      setConsent(await getUserYieldConsent(userId));
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const toggle = async () => {
    setBusy(true);
    try {
      const next = !(consent?.opted_in);
      const updated = await updateUserYieldConsent(userId, next);
      setConsent(updated);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="profile-page" className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center gap-2">
        <User size={18} style={{ color: "var(--yb-gold)" }} />
        <h1 className="text-2xl font-black tracking-tight" style={{ color: "var(--yb-text-primary)" }}>
          Profile
        </h1>
      </div>

      <div className="rounded-sm p-4" style={{ background: "var(--yb-surface-1)", border: "1px solid var(--yb-border)" }}>
        <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--yb-text-muted)" }}>User ID</p>
        <p className="text-sm font-mono" style={{ color: "var(--yb-text-primary)" }}>{userId}</p>
      </div>

      <div data-testid="yield-consent-card" className="rounded-sm p-5"
        style={{ background: "rgba(255,184,0,0.04)", border: "1px solid rgba(255,184,0,0.2)" }}>
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={16} style={{ color: "var(--yb-gold)" }} />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--yb-gold)" }}>
            Yield on My Balance
          </span>
        </div>
        <div className="rounded-sm p-3 mb-4 flex items-start gap-2"
          style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" }}>
          <AlertTriangle size={14} className="shrink-0 mt-0.5" style={{ color: "#ef4444" }} />
          <p className="text-[11px]" style={{ color: "var(--yb-text-secondary)" }}>
            <span className="font-bold">Coming soon — pending legal terms.</span> Toggling this records
            your consent only. Funds are not moved by autonomous systems while AFS/AUSTRAC review is in
            progress. The yield router operates exclusively on operator own-funds today.
          </p>
        </div>
        {err && <p className="text-xs font-mono mb-2" style={{ color: "#ef4444" }}>{err}</p>}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--yb-text-primary)" }}>
              Consent: <span className="font-mono" style={{ color: consent?.opted_in ? "#22c55e" : "var(--yb-text-muted)" }}>
                {consent?.opted_in ? "OPTED-IN" : "OPTED-OUT"}
              </span>
            </p>
            {consent?.opted_in_at && (
              <p className="text-[10px] font-mono mt-0.5" style={{ color: "var(--yb-text-muted)" }}>
                Recorded: {consent.opted_in_at}
              </p>
            )}
          </div>
          <button data-testid="btn-toggle-yield-consent" onClick={toggle} disabled={busy}
            className="text-xs font-bold px-4 py-2 rounded-sm"
            style={{
              background: consent?.opted_in ? "rgba(239,68,68,0.1)" : "var(--yb-gold)",
              color: consent?.opted_in ? "#ef4444" : "#000",
              border: consent?.opted_in ? "1px solid rgba(239,68,68,0.3)" : "1px solid var(--yb-gold)",
              opacity: busy ? 0.5 : 1,
            }}>
            {busy ? "..." : consent?.opted_in ? "Opt out" : "Opt in (consent only)"}
          </button>
        </div>
      </div>
    </div>
  );
}
